import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { useDashReport } from '../hooks/useDashReport'
import type { AbbottSegmentoReport, AbbottSegmento, ResumenCiudad } from '../types'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const LOGO_URL = '/logo-tonal-negro.png'

const colores: Record<string, { bg: string; border: string }> = {
  danilo: { bg: 'rgba(59, 130, 246, 0.7)', border: '#3b82f6' },
  fernando: { bg: 'rgba(16, 185, 129, 0.7)', border: '#10b981' },
  mabe: { bg: 'rgba(139, 92, 246, 0.7)', border: '#8b5cf6' },
  todos: { bg: 'rgba(99, 102, 241, 0.7)', border: '#6366f1' },
}

export function AbbottSegmentoPage() {
  const { data, isLoading, error } = useDashReport<AbbottSegmentoReport>('abbott/segmento-1225')

  const [filtroActual, setFiltroActual] = useState('todos')
  const [filtroEspecialidad, setFiltroEspecialidad] = useState('todas')

  const [expandedEspecialidades, setExpandedEspecialidades] = useState(false)
  const [expandedCiudades, setExpandedCiudades] = useState(false)

  const contentEspRef = useRef<HTMLDivElement>(null)
  const contentCiudRef = useRef<HTMLDivElement>(null)

  // Recalculate max-height when content changes (filters change table rows)
  useEffect(() => {
    if (expandedEspecialidades && contentEspRef.current) {
      contentEspRef.current.style.maxHeight = contentEspRef.current.scrollHeight + 'px'
    }
  }, [expandedEspecialidades, filtroActual, filtroEspecialidad])

  useEffect(() => {
    if (expandedCiudades && contentCiudRef.current) {
      contentCiudRef.current.style.maxHeight = contentCiudRef.current.scrollHeight + 'px'
    }
  }, [expandedCiudades, filtroActual, filtroEspecialidad])

  const handleFiltrarRepresentante = useCallback((repId: string) => {
    setFiltroActual(repId)
    setFiltroEspecialidad('todas')
  }, [])

  const handleFiltrarEspecialidad = useCallback((esp: string) => {
    setFiltroEspecialidad(esp)
  }, [])

  // Especialidades del representante seleccionado
  const especialidadesRep = useMemo(() => {
    if (!data || filtroActual === 'todos') return []
    return data.segmentos
      .filter((s) => s.representante_id === filtroActual)
      .map((s) => s.especialidad)
  }, [data, filtroActual])

  // Segmentos filtrados
  const segmentosFiltrados = useMemo(() => {
    if (!data) return []
    let filtered = data.segmentos
    if (filtroActual !== 'todos') {
      filtered = filtered.filter((s) => s.representante_id === filtroActual)
    }
    if (filtroEspecialidad !== 'todas') {
      filtered = filtered.filter((s) => s.especialidad === filtroEspecialidad)
    }
    return filtered
  }, [data, filtroActual, filtroEspecialidad])

  // Datos de ciudad (dinámicos según filtro)
  const dataCiudad = useMemo(() => {
    if (!data) return []
    if (filtroActual === 'todos') {
      return data.resumen_por_ciudad.map((c) => ({
        label: c.ciudad,
        value: c.total,
      }))
    }
    // Agregar ciudades de los segmentos filtrados
    const ciudadesMap: Record<string, number> = {}
    segmentosFiltrados.forEach((seg: AbbottSegmento) => {
      seg.distribucion.forEach((d) => {
        if (!ciudadesMap[d.ciudad]) ciudadesMap[d.ciudad] = 0
        ciudadesMap[d.ciudad] += d.cantidad
      })
    })
    return Object.entries(ciudadesMap)
      .map(([ciudad, total]) => ({ label: ciudad, value: total }))
      .sort((a, b) => b.value - a.value)
  }, [data, filtroActual, segmentosFiltrados])

  // Tabla de ciudades con porcentaje
  const tablaCiudades = useMemo(() => {
    if (!data) return []
    if (filtroActual === 'todos') {
      return data.resumen_por_ciudad.map((c: ResumenCiudad) => ({
        ciudad: c.ciudad,
        total: c.total,
        porcentaje: c.porcentaje,
      }))
    }
    const totalFiltrado = dataCiudad.reduce((sum, c) => sum + c.value, 0) || 1
    return dataCiudad.map((c) => ({
      ciudad: c.label,
      total: c.value,
      porcentaje: parseFloat(((c.value / totalFiltrado) * 100).toFixed(1)),
    }))
  }, [data, filtroActual, dataCiudad])

  // Titulo y label del resumen
  const tituloResumen = useMemo(() => {
    if (!data || filtroActual === 'todos') return 'Resumen General'
    const rep = data.representantes.find((r) => r.id === filtroActual)
    return rep ? `${rep.nombre} - ${rep.linea}` : 'Resumen General'
  }, [data, filtroActual])

  const labelTotal = useMemo(() => {
    if (!data) return 'Total Base de Datos'
    if (filtroActual === 'todos') return 'Total Base de Datos'
    if (filtroEspecialidad !== 'todas') return filtroEspecialidad
    const rep = data.representantes.find((r) => r.id === filtroActual)
    return rep ? `Total ${rep.linea}` : 'Total Base de Datos'
  }, [data, filtroActual, filtroEspecialidad])

  const totalMostrado = useMemo(() => {
    if (!data) return 0
    if (filtroActual === 'todos') return data.total_general
    if (filtroEspecialidad !== 'todas') {
      const segEsp = data.segmentos.find(
        (s) => s.representante_id === filtroActual && s.especialidad === filtroEspecialidad
      )
      return segEsp ? segEsp.total : 0
    }
    const rep = data.representantes.find((r) => r.id === filtroActual)
    return rep ? rep.total_representante : 0
  }, [data, filtroActual, filtroEspecialidad])

  // Filas visibles de tabla especialidades
  const filasEspecialidades = useMemo(() => {
    if (!data) return []
    return data.segmentos.filter((seg) => {
      if (filtroActual !== 'todos' && seg.representante_id !== filtroActual) return false
      if (filtroEspecialidad !== 'todas' && seg.especialidad !== filtroEspecialidad) return false
      return true
    })
  }, [data, filtroActual, filtroEspecialidad])

  // Toggle seccion
  const toggleSeccion = (seccion: 'especialidades' | 'ciudades') => {
    if (seccion === 'especialidades') {
      const next = !expandedEspecialidades
      setExpandedEspecialidades(next)
      if (contentEspRef.current) {
        if (next) {
          contentEspRef.current.style.maxHeight = contentEspRef.current.scrollHeight + 'px'
          contentEspRef.current.style.opacity = '1'
        } else {
          contentEspRef.current.style.maxHeight = '0px'
          contentEspRef.current.style.opacity = '0'
        }
      }
    } else {
      const next = !expandedCiudades
      setExpandedCiudades(next)
      if (contentCiudRef.current) {
        if (next) {
          contentCiudRef.current.style.maxHeight = contentCiudRef.current.scrollHeight + 'px'
          contentCiudRef.current.style.opacity = '1'
        } else {
          contentCiudRef.current.style.maxHeight = '0px'
          contentCiudRef.current.style.opacity = '0'
        }
      }
    }
  }

  // Rep color helper
  const repColor = (index: number) => {
    if (index === 0) return '#3b82f6'
    if (index === 1) return '#10b981'
    return '#8b5cf6'
  }

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center py-32">
          <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
        </div>
      </div>
    )
  }

  // Chart data: especialidad (horizontal bars)
  const dataEspecialidad = segmentosFiltrados.map((s) => ({
    label: s.especialidad,
    value: s.total,
    rep: s.representante_id,
  }))

  const chartEspecialidadData = {
    labels: dataEspecialidad.map((d) => d.label),
    datasets: [
      {
        label: 'Total',
        data: dataEspecialidad.map((d) => d.value),
        backgroundColor: dataEspecialidad.map(
          (d) => colores[d.rep]?.bg || colores.todos.bg
        ),
        borderColor: dataEspecialidad.map(
          (d) => colores[d.rep]?.border || colores.todos.border
        ),
        borderWidth: 1,
      },
    ],
  }

  const chartEspecialidadOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { right: 80 },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'right' as const,
        offset: 4,
        clip: false,
        color: '#6b7280',
        font: {
          size: 11,
          weight: 500 as const,
        },
        formatter: function (value: number) {
          return value.toLocaleString('es-EC')
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grace: '5%',
        ticks: {
          callback: function (value: number | string) {
            return Number(value).toLocaleString('es-EC')
          },
        },
      },
    },
  }

  // Chart data: ciudad (vertical bars, top 10)
  const top10Ciudades = dataCiudad.slice(0, 10)

  const chartCiudadData = {
    labels: top10Ciudades.map((d) => d.label),
    datasets: [
      {
        label: 'Total',
        data: top10Ciudades.map((d) => d.value),
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderColor: '#10b981',
        borderWidth: 1,
      },
    ],
  }

  const chartCiudadOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20 },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'top' as const,
        offset: 2,
        clip: false,
        color: '#6b7280',
        font: {
          size: 10,
          weight: 500 as const,
        },
        formatter: function (value: number) {
          return value.toLocaleString('es-EC')
        },
      },
    },
    scales: {
      y: {
        position: 'right' as const,
        beginAtZero: true,
        grace: '10%',
        ticks: {
          callback: function (value: number | string) {
            return Number(value).toLocaleString('es-EC')
          },
        },
      },
    },
  }

  return (
    <>
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
        .animate-pulse-subtle.expanded { animation: none; opacity: 1; transform: scale(1); }

        /* Estilos de botones de filtro */
        .filter-btn.active {
          background-color: #2563eb !important;
          color: white !important;
        }
        .filter-btn.active:hover {
          background-color: #1d4ed8 !important;
        }
        .filter-btn:not(.active):hover {
          background-color: #e5e7eb;
        }

        /* Estilos de botones de filtro especialidad */
        .filter-esp-btn.active {
          background-color: #2563eb !important;
          color: white !important;
        }
        .filter-esp-btn.active:hover {
          background-color: #1d4ed8 !important;
        }
        .filter-esp-btn:not(.active):hover {
          background-color: #e5e7eb;
        }
      `}</style>

      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center">
                <img
                  src={LOGO_URL}
                  alt="Tonal"
                  className="h-8 lg:h-10 w-auto mr-4 lg:mr-6"
                />
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">
                    {data.titulo}
                  </h1>
                  <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href="https://enterprise.tonalstrategies.com/pricing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
                >
                  Calculador
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Filtro de Representante */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-blue-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                <span className="font-semibold text-gray-900">Filtrar por:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleFiltrarRepresentante('todos')}
                  className={`filter-btn px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700 ${filtroActual === 'todos' ? 'active' : ''}`}
                >
                  Todos
                </button>
                {data.representantes.map((rep) => (
                  <button
                    key={rep.id}
                    onClick={() => handleFiltrarRepresentante(rep.id)}
                    className={`filter-btn px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700 ${filtroActual === rep.id ? 'active' : ''}`}
                  >
                    {rep.nombre}{' '}
                    <span className="text-xs opacity-75">({rep.linea})</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Filtro de Especialidad (oculto por defecto) */}
            {filtroActual !== 'todos' && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-indigo-600 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <span className="font-semibold text-gray-900">Especialidad:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleFiltrarEspecialidad('todas')}
                      className={`filter-esp-btn px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700 ${filtroEspecialidad === 'todas' ? 'active' : ''}`}
                    >
                      Todas
                    </button>
                    {especialidadesRep.map((esp, idx) => (
                      <button
                        key={`esp-${idx}`}
                        onClick={() => handleFiltrarEspecialidad(esp)}
                        className={`filter-esp-btn px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700 ${filtroEspecialidad === esp ? 'active' : ''}`}
                      >
                        {esp}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Estadisticas Generales */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
              <svg
                className="w-5 h-5 text-blue-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <span>{tituloResumen}</span>
            </h2>

            {/* Total General */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm sm:col-span-1">
                <p className="text-3xl lg:text-4xl font-bold text-gray-900">
                  {totalMostrado.toLocaleString('es-EC')}
                </p>
                <p className="text-sm text-gray-600 mt-1">{labelTotal}</p>
              </div>
              {data.representantes.map((rep, idx) => (
                <div
                  key={rep.id}
                  className="bg-white rounded-lg px-4 py-3 text-center shadow-sm"
                  style={{
                    display:
                      filtroActual === 'todos' || filtroActual === rep.id
                        ? 'block'
                        : 'none',
                  }}
                >
                  <p
                    className="text-2xl lg:text-3xl font-bold"
                    style={{ color: repColor(idx) }}
                  >
                    {rep.total_representante}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{rep.nombre}</p>
                  <p className="text-xs text-gray-500">{rep.linea}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Graficas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Grafica por Especialidad */}
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg
                  className="w-5 h-5 text-indigo-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                Distribucion por Especialidad
              </h3>
              <div className="h-64 lg:h-80">
                <Bar
                  data={chartEspecialidadData}
                  options={chartEspecialidadOptions}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>

            {/* Grafica por Ciudad */}
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg
                  className="w-5 h-5 text-green-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Distribucion por Ciudad
              </h3>
              <div className="h-64 lg:h-80">
                <Bar
                  data={chartCiudadData}
                  options={chartCiudadOptions}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </div>
          </div>

          {/* Tabla de Detalle por Especialidad */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <button
              onClick={() => toggleSeccion('especialidades')}
              className="w-full text-left focus:outline-none group py-3 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
            >
              <div className="font-semibold text-gray-900 flex items-center justify-between">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-indigo-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <span className="text-base lg:text-lg">Detalle por Especialidad</span>
                  <span className="text-xs lg:text-sm text-gray-500 ml-2">
                    ({filasEspecialidades.length} segmentos)
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 ${expandedEspecialidades ? 'rotate-180 expanded' : 'animate-pulse-subtle'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            <div
              ref={contentEspRef}
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: 0, opacity: 0 }}
            >
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Representante
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Linea
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Especialidad
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filasEspecialidades.map((seg, idx) => (
                      <tr key={`${seg.representante_id}-${seg.especialidad}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {seg.representante}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{seg.linea}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {seg.especialidad}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">
                          {seg.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Tabla de Detalle por Ciudad */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <button
              onClick={() => toggleSeccion('ciudades')}
              className="w-full text-left focus:outline-none group py-3 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
            >
              <div className="font-semibold text-gray-900 flex items-center justify-between">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-green-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-base lg:text-lg">Detalle por Ciudad</span>
                  <span className="text-xs lg:text-sm text-gray-500 ml-2">
                    ({tablaCiudades.length} ciudades)
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 ${expandedCiudades ? 'rotate-180 expanded' : 'animate-pulse-subtle'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            <div
              ref={contentCiudRef}
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: 0, opacity: 0 }}
            >
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Ciudad
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Total
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        % del Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tablaCiudades.map((ciudad, idx) => (
                      <tr key={`${ciudad.ciudad}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {ciudad.ciudad}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                          {ciudad.total.toLocaleString('es-EC')}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">
                          {ciudad.porcentaje.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 py-6 text-center">
          <div className="text-xs text-gray-400">
            <a
              href="https://enterprise.tonalstrategies.com"
              className="hover:text-gray-600 transition-colors"
            >
              Tonal Enterprise
            </a>
            <span className="mx-2">&bull;</span>
            <span>2025 Tonal Strategies</span>
          </div>
        </footer>
      </div>
    </>
  )
}
